/**
 * Browser-and-server-safe event helpers. No Supabase imports here so both the
 * public route and the admin UI can share the shapes and naming rules.
 */

export const EVENT_STATUSES = ["draft", "planned", "ended", "cancelled"] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

/**
 * What kind of night this is. Separate from `host_kind`: a community event
 * (not run by SquadFlight) can be any of these.
 */
export const EVENT_TYPES = [
  "training",
  "track_session",
  "freestyle_session",
  "meetup",
  "league_session",
  "proving_ground",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  training: "Training",
  track_session: "Track Session",
  freestyle_session: "Freestyle Session",
  meetup: "Meetup",
  league_session: "League Session",
  proving_ground: "Proving Ground",
};

/**
 * Types only crew/admin may set. Hosts can see the label on an existing event
 * but cannot pick it, and the server rejects it if they try anyway.
 */
export const CREW_ONLY_EVENT_TYPES: readonly EventType[] = ["proving_ground"];

export function isCrewOnlyEventType(value: string | null | undefined): boolean {
  return CREW_ONLY_EVENT_TYPES.includes(value as EventType);
}

/** What a host may choose, keeping whatever the event already is in the list. */
export function hostSelectableEventTypes(current?: string | null): EventType[] {
  return EVENT_TYPES.filter((t) => !isCrewOnlyEventType(t) || t === current);
}

export const DEFAULT_EVENT_TYPE: EventType = "track_session";

export function eventTypeLabel(value: string | null | undefined): string {
  return EVENT_TYPE_LABELS[(value ?? DEFAULT_EVENT_TYPE) as EventType] ?? "Session";
}

/**
 * One plain-English line saying what the night actually is, so a card scans
 * without opening it. Shown above the description snippet on the list.
 */
export const EVENT_TYPE_HOOKS: Record<EventType, string> = {
  training: "Coached practice — bring questions, leave flying better",
  track_session: "Legal, ready-to-fly track — fly with your friends, old and new",
  freestyle_session: "Open air, no clock — flow, dives and tricks",
  meetup: "Hang out, swap builds, casual flying",
  league_session: "Head-to-head league racing with standings on the line",
  proving_ground: "First flight session — gear and coaching provided",
};

export function eventTypeHook(value: string | null | undefined): string {
  return (
    EVENT_TYPE_HOOKS[(value ?? DEFAULT_EVENT_TYPE) as EventType] ?? "An FPV night in the valley"
  );
}

/** Short scarcity pill for a card. `tone` drives the color treatment. */
export function seatsPill(
  capacity: number | undefined,
  going: number | undefined,
): { text: string; tone: "hot" | "warm" | "calm" } | null {
  if (!capacity || capacity <= 0) return null;
  const taken = Math.max(0, going ?? 0);
  const left = Math.max(0, capacity - taken);
  if (left === 0) return { text: "Waitlist open", tone: "hot" };
  if (left <= 5) return { text: `${left} spot${left === 1 ? "" : "s"} left`, tone: "hot" };
  if (left <= Math.ceil(capacity / 2)) return { text: `${left} spots left`, tone: "warm" };
  return { text: `${left} spots open`, tone: "calm" };
}

/**
 * Sensible starting points when a host picks a type. Hosts can still override
 * either switch afterwards — nothing here is enforced.
 */
export function defaultsForEventType(type: EventType): {
  requires_freq_allocation: boolean;
  is_intro_session: boolean;
} {
  switch (type) {
    case "training":
    case "proving_ground":
      return { requires_freq_allocation: false, is_intro_session: true };
    default:
      return { requires_freq_allocation: true, is_intro_session: false };
  }
}

/**
 * Where people actually RSVP. `internal` is our own signup flow; anything else
 * means we're a directory for that night and just link out.
 */
export const RSVP_MODES = [
  "internal",
  "none",
  "partiful",
  "facebook",
  "meetup",
  "multigp",
] as const;
export type RsvpMode = (typeof RSVP_MODES)[number];

/** Modes that send people off-site to sign up. */
export type ExternalRsvpMode = Exclude<RsvpMode, "internal" | "none">;

export const RSVP_MODE_LABELS: Record<RsvpMode, string> = {
  internal: "SquadFlight (our RSVP)",
  none: "None. Just show up!",
  partiful: "Partiful",
  facebook: "Facebook",
  meetup: "Meetup",
  multigp: "MultiGP",
};

/** Which link field each external mode reads from. */
export const RSVP_MODE_URL_FIELD: Record<
  ExternalRsvpMode,
  "partiful_url" | "facebook_event_url" | "meetup_event_url" | "multigp_event_url"
> = {
  partiful: "partiful_url",
  facebook: "facebook_event_url",
  meetup: "meetup_event_url",
  multigp: "multigp_event_url",
};

type RsvpLinkFields = {
  rsvp_mode?: string | null;
  partiful_url?: string | null;
  facebook_event_url?: string | null;
  meetup_event_url?: string | null;
  multigp_event_url?: string | null;
};

/** No signup at all: open-door night, nobody collects a list. */
export function isNoRsvpEvent(event: { rsvp_mode?: string | null }): boolean {
  return (event.rsvp_mode ?? "internal") === "none";
}

/** Only our own RSVP flow has a pilot limit; every other mode is managed elsewhere. */
export function managesSeats(event: { rsvp_mode?: string | null }): boolean {
  return (event.rsvp_mode ?? "internal") === "internal";
}

/** Capacity to store: a real limit (default 8) for internal, null otherwise. */
export function capacityForMode(
  mode: string | null | undefined,
  raw: string | number | null | undefined,
): number | null {
  if ((mode ?? "internal") !== "internal") return null;
  return Number(raw) || 8;
}

/** Where signups happen when we don't manage them, e.g. "RSVP on MultiGP". */
export function signupDestinationLabel(mode: string | null | undefined): string {
  const m = (mode ?? "internal") as RsvpMode;
  if (m === "none") return "Open door";
  if (m === "internal") return "SquadFlight RSVP";
  return `RSVP on ${RSVP_MODE_LABELS[m] ?? m}`;
}

/**
 * The outbound RSVP link for an event, or null when we own the RSVP. A mode
 * with a missing link falls back to our own flow so nobody hits a dead end.
 */
export function externalRsvp(
  event: RsvpLinkFields,
): { mode: ExternalRsvpMode; label: string; url: string } | null {
  const mode = (event.rsvp_mode ?? "internal") as RsvpMode;
  if (mode === "internal" || mode === "none") return null;
  const key = RSVP_MODE_URL_FIELD[mode];
  if (!key) return null;
  const url = event[key]?.trim();
  if (!url) return null;
  return { mode, label: RSVP_MODE_LABELS[mode], url };
}

export type SquadEvent = {
  id: string;
  campaign_id: string | null;
  title: string;
  /** One-line subheadline shown under the title. Optional. */
  subtitle?: string | null;
  slug: string;
  description: string | null;
  location: string | null;
  starts_at: string;
  ends_at: string | null;
  /** Cross-post links. Also the target when RSVPs live off-site. */
  partiful_url: string | null;
  facebook_event_url?: string | null;
  meetup_event_url?: string | null;
  eventbrite_url?: string | null;
  posh_url?: string | null;
  multigp_event_url?: string | null;
  /** `internal` = our RSVP flow; anything else links out to that platform. */
  rsvp_mode?: RsvpMode | string | null;
  cover_image_url: string | null;
  status: string;
  featured: boolean;
  /** Pilot limit. Spectators have their own limit. */
  capacity?: number;
  spectator_capacity?: number;
  /** Community hosts can run the FREQ board for their own night. */
  freq_enabled?: boolean;
  /** Master switch for the FREQ board. False means no racebands are handed out. */
  requires_freq_allocation?: boolean;
  /** What kind of night this is. Independent of who runs it. */
  event_type?: EventType;
  /** Test night: hidden from the public list and left out of every report. */
  is_test?: boolean;

  /** Paid seats: 0 means free. */
  price_cents?: number;
  /** Optional hardware add-on sold alongside the seat. 0 means none offered. */
  kit_price_cents?: number;
  kit_label?: string | null;
  /** Staff-configured Stripe Payment Link for the seat only. */
  payment_link_url?: string | null;
  /** Staff-configured Stripe Payment Link for seat + kit bundle. */
  payment_link_with_kit_url?: string | null;
  /** Kit-only checkout, offered after the seat is paid. */
  kit_only_payment_link_url?: string | null;
  /** Reduced-price seat checkout, used when a discount code is applied. */
  discount_payment_link_url?: string | null;
  /** Training ladder: paying for this class grants this code kind. */
  grants_pass_kind?: string | null;
  grants_pass_discount_cents?: number | null;
  /** Code kinds this class accepts at reservation. */
  accepted_pass_kinds?: string[] | null;
  /** This night's held last-call email in Loops. Linked from the low-seat alert. */
  loops_last_call_url?: string | null;
  /** Ask for photo/video consent on the signup form. */
  photo_consent_required?: boolean;
  /** Free "Proving Ground Intro" session — surfaced on the /start flyer page. */
  is_intro_session?: boolean;

  /** `squadflight` = our event; `community` = hosted by a pilot. */
  host_kind?: "squadflight" | "community";
  host_pilot_id?: string | null;
  /** Repeating-series linkage for generated event nights. */
  series_id?: string | null;
  series_occurrence_date?: string | null;
  detached_from_series?: boolean;
  created_at: string;
  updated_at: string;
};

/** RSVP roll-up shown on every management row. */
export type EventCounts = {
  going: number;
  waitlisted: number;
  spectators: number;
};

export type ManagedEvent = SquadEvent & {
  host_name?: string | null;
  counts?: EventCounts;
};

export const EVENT_TIME_FILTERS = ["upcoming", "past", "all"] as const;
export const EVENT_TYPE_FILTERS = ["all", "squadflight", "community"] as const;
export const EVENT_SORTS = ["date", "title", "rsvps"] as const;

export type EventListFilters = {
  q?: string;
  time?: string;
  status?: string;
  type?: string;
  /** Event type: training, track_session, ... */
  etype?: string;
  sort?: string;
};

/** Pure list pipeline shared by the admin/crew manager and the host dashboard. */
export function filterAndSortEvents<T extends ManagedEvent>(
  events: T[],
  filters: EventListFilters,
  now = Date.now(),
): T[] {
  const q = (filters.q ?? "").trim().toLowerCase();
  const time = filters.time ?? "upcoming";
  const status = filters.status ?? "all";
  const type = filters.type ?? "all";
  const etype = filters.etype ?? "all";
  const sort = filters.sort ?? "date";

  const rows = events.filter((e) => {
    if (status !== "all" && e.status !== status) return false;
    if (type !== "all") {
      const kind = e.host_kind ?? "squadflight";
      if (kind !== type) return false;
    }
    if (etype !== "all" && (e.event_type ?? DEFAULT_EVENT_TYPE) !== etype) return false;
    if (time === "upcoming" && isPast(e, now)) return false;
    if (time === "past" && !isPast(e, now)) return false;
    if (q) {
      const hay = [e.title, e.location, e.slug, e.host_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const byDateAsc = (a: T, b: T) => +new Date(a.starts_at) - +new Date(b.starts_at);
  const byDateDesc = (a: T, b: T) => -byDateAsc(a, b);

  if (sort === "title") return rows.sort((a, b) => a.title.localeCompare(b.title));
  if (sort === "rsvps")
    return rows.sort((a, b) => (b.counts?.going ?? 0) - (a.counts?.going ?? 0) || byDateAsc(a, b));
  return rows.sort(time === "past" ? byDateDesc : byDateAsc);
}

export function isCommunityEvent(event: {
  host_kind?: "squadflight" | "community" | null | undefined;
}): boolean {
  return event.host_kind === "community";
}

/** `2026-09-14` in UTC — stable regardless of who is looking at it. */
/** Calendar day at the venue (Las Vegas), not UTC — evening sessions must not roll over. */
const VENUE_TZ = "America/Los_Angeles";

export function isoDate(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: VENUE_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Short venue-local date, e.g. `Sep 25`. */
export function venueShortDate(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: VENUE_TZ,
    month: "short",
    day: "numeric",
  }).format(d);
}

/** Campaign naming rule: `yyyy-mm-dd Event Name`. */
export function campaignNameForEvent(startsAt: string, title: string): string {
  return `${isoDate(startsAt)} ${title.trim()}`.trim();
}

export function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** Past once the end time (or the start, when open-ended) has passed. */
export function isPast(
  event: Pick<SquadEvent, "starts_at" | "ends_at">,
  now = Date.now(),
): boolean {
  const end = event.ends_at ?? event.starts_at;
  return new Date(end).getTime() < now;
}

/**
 * The arena's wall-clock timezone. Every public date string is rendered in it
 * so the server (UTC) and the visitor's browser produce identical HTML — a
 * mismatch here throws a React hydration error and can break the page's
 * event handlers (including the waitlist form).
 */
export const VENUE_TIME_ZONE = "America/Los_Angeles";

export function formatEventDate(startsAt: string, endsAt?: string | null): string {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return "";
  const date = start.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: VENUE_TIME_ZONE,
  });
  const time = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: VENUE_TIME_ZONE,
  });
  if (!endsAt) return `${date} · ${time}`;
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return `${date} · ${time}`;
  const endTime = end.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: VENUE_TIME_ZONE,
  });
  return `${date} · ${time}–${endTime}`;
}

/** Calendar day (yyyy-mm-dd) in arena time, stable on server and client. */
function venueDayNumber(at: number): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: VENUE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(at));
  return Math.floor(Date.parse(`${parts}T00:00:00Z`) / 86_400_000);
}

/**
 * Human proximity label used on public event cards — "This Friday" reads as an
 * invitation, "Sep 14" reads as a database row. Calendar-day based in arena
 * time so it never differs between the server render and the browser.
 */
export function proximityLabel(startsAt: string, now = Date.now()): string {
  const start = new Date(startsAt).getTime();
  if (Number.isNaN(start)) return "";
  const days = venueDayNumber(start) - venueDayNumber(now);
  if (days < -1) return `${Math.abs(days)} days ago`;
  if (days === -1) return "Yesterday";
  if (days === 0) {
    const hour = Number(
      new Intl.DateTimeFormat("en-US", {
        timeZone: VENUE_TIME_ZONE,
        hour: "numeric",
        hourCycle: "h23",
      }).format(new Date(start)),
    );
    return hour >= 17 ? "Tonight" : "Today";
  }
  if (days === 1) return "Tomorrow";
  if (days < 7)
    return `This ${new Date(startsAt).toLocaleDateString("en-US", {
      weekday: "long",
      timeZone: VENUE_TIME_ZONE,
    })}`;
  if (days < 14) return "Next week";
  return `In ${days} days`;
}

/** Scarcity copy for a public card. Returns null when there is nothing honest to say. */
export function seatsLabel(capacity: number | undefined, going: number | undefined): string | null {
  if (!capacity || capacity <= 0) return null;
  const taken = Math.max(0, going ?? 0);
  const left = Math.max(0, capacity - taken);
  if (left === 0) return "Pilot spots full — join the waitlist";
  if (left <= 5) return `Only ${left} pilot ${left === 1 ? "spot" : "spots"} left`;
  return `${taken} of ${capacity} pilot spots taken`;
}

/** How full a night's pilot seats are — the crew/admin seat meter's color cue. */
export type SeatTone = "empty" | "open" | "filling" | "full";

export type SeatFill = {
  taken: number;
  capacity: number;
  left: number;
  /** 0–100, clamped so an over-booked night still reads as full. */
  pct: number;
  tone: SeatTone;
};

/**
 * Pilot-seat fill for one event. Returns null when no capacity is set, so a
 * caller can skip the meter rather than render a meaningless `0/0`.
 */
export function seatFill(
  capacity: number | null | undefined,
  going: number | null | undefined,
): SeatFill | null {
  const cap = capacity ?? 0;
  if (cap <= 0) return null;
  const taken = Math.max(0, going ?? 0);
  const left = Math.max(0, cap - taken);
  const pct = Math.min(100, Math.round((taken / cap) * 100));
  const tone: SeatTone =
    taken === 0 ? "empty" : left === 0 ? "full" : pct >= 75 ? "filling" : "open";
  return { taken, capacity: cap, left, pct, tone };
}

/** Roll-up across a list of events, for the summary strip above the rows. */
export type EventTotals = {
  events: number;
  going: number;
  waitlisted: number;
  spectators: number;
  capacity: number;
  seatsLeft: number;
};

/**
 * Sum RSVP counts over a filtered list. Seats left is floored per event, so one
 * over-booked night can't hide a shortage on another.
 */
export function summarizeEvents<T extends ManagedEvent>(events: T[]): EventTotals {
  const totals: EventTotals = {
    events: events.length,
    going: 0,
    waitlisted: 0,
    spectators: 0,
    capacity: 0,
    seatsLeft: 0,
  };
  for (const e of events) {
    const taken = Math.max(0, e.counts?.going ?? 0);
    totals.going += taken;
    totals.waitlisted += Math.max(0, e.counts?.waitlisted ?? 0);
    totals.spectators += Math.max(0, e.counts?.spectators ?? 0);
    const cap = e.capacity ?? 0;
    if (cap > 0) {
      totals.capacity += cap;
      totals.seatsLeft += Math.max(0, cap - taken);
    }
  }
  return totals;
}

/** `24900` -> `$249`, `2450` -> `$24.50`. Used for paid seats and kits. */
export function usd(cents: number): string {
  const dollars = (cents ?? 0) / 100;
  return `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
}

/** Month heading for grouping a list of events, in venue time. */
export function formatEventMonth(startsAt: string): string {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return "";
  return start.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: VENUE_TIME_ZONE,
  });
}
